import { type JSX } from "react";
import { Placeholder, RichText, Text } from "@sitecore-content-sdk/nextjs";
import { ModalProps } from "components/Modal/modal.props";
import { extractHiddenBlock } from "../../utils/styles";
import { NoDataFallback } from "../../utils/NoDataFallback";
import ModalDialog from "./ModalDialog";
import styles from "./Modal.module.scss";
import { POSITIONAL_CLASSES, SPACING_CLASSES } from "src/variables/styles";

const SIZE_TO_MAX_WIDTH: Record<string, string> = {
  small: "max-w-[300px]",
  medium: "max-w-[500px]",
  large: "max-w-[800px]",
};

const FALLBACK_POSITION = "top-[10%] left-1/2 -translate-x-1/2";
const DEFAULT_BUTTON_STYLE = "primary-reverse";

const buttonVariants = [
  "primary",
  "primary-reverse",
  "secondary",
  "secondary-reverse",
  "tertiary",
  "tertiary-reverse",
  "numbers",
  "primary-with-icon",
  "secondary-with-icon",
  "tertiary-reverse",
  "tertiary-with-left-side-icon",
  "tertiary-with-right-side-icon",
  "navigation-tertiary-reverse",
];

export const Default = (props: ModalProps): JSX.Element => {
  console.log("---->[Modal] Component", props.fields);

  const { fields, params, rendering, page } = props;

  if (!fields) {
    return <NoDataFallback componentName="Modal" />;
  }

  const phKey = `modal-${params?.DynamicPlaceholderId}`;
  const sizeClass =
    SIZE_TO_MAX_WIDTH[params?.Size?.toLowerCase() ?? ""] ?? "max-w-[300px]";

  const styleTokens = params?.Styles?.split(" ") ?? [];

  const resolvedPosition = styleTokens.find((cls) =>
    POSITIONAL_CLASSES.includes(cls),
  );
  const resolvedSpacing = styleTokens.filter((cls) =>
    SPACING_CLASSES.includes(cls),
  );

  const positionClass = resolvedPosition
    ? styles[resolvedPosition]
    : FALLBACK_POSITION;
  const spacingClassNames = resolvedSpacing.map((cls) => styles[cls]).join(" ");
  const buttonPositionClass = resolvedPosition
    ? (styles[`btn-${resolvedPosition}`] ?? "")
    : "";

  const buttonStyle =
    styleTokens.find((token) => buttonVariants.includes(token.toLowerCase())) ||
    DEFAULT_BUTTON_STYLE;

  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  if (page?.mode?.isEditing) {
    return (
      <div className="p-2 border border-dashed border-dots-gray w-full my-2">
        <Text
          field={fields.ModalIdentifier}
          tag="span"
          className={`mt-2 line-clamp-1 max-w-[32ch] truncate`}
        />
        <div
          className={`w-[90%] bg-white p-6 md:p-8 rounded-md shadow-lg border-none outline-none max-w-[520px] ${sizeClass} mx-auto my-4`}
        >
          <RichText
            tag="h2"
            field={fields.Title}
            className="text-xl font-semibold mb-4 modal-header rte-content richtext-content"
          />
          <div className="min-h-32">
            <Placeholder name={phKey} rendering={rendering} />
          </div>
          <RichText
            field={fields.Footer}
            className={
              "pt-4 border-t border-gray-200 flex gap-2 richtext-content"
            }
          />
        </div>
      </div>
    );
  }

  return (
    <ModalDialog
      modalIdentifier={fields.ModalIdentifier?.value}
      title={fields.Title}
      footer={fields.Footer}
      sizeClass={sizeClass}
      positionClass={positionClass}
      spacingClassNames={spacingClassNames}
      buttonStyle={buttonStyle}
      buttonPositionClass={buttonPositionClass}
      hiddenBlockStyles={hiddenBlockStyles}
    >
      <Placeholder name={phKey} rendering={rendering} />
    </ModalDialog>
  );
};
